import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import type { UnifiedContactResponse, ContactListPage as ContactListPageResponse } from "../../../types/crm";
import { getContacts, exportContactEmailList } from "../../../services/crmContacts";
import { DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "../../../constants/extraction";
import { downloadBlobFile } from "../../../utils/file-download";
import { getSafeErrorMessage } from "../../../utils/errorHandler";
import { ContactsToolbar } from "./ContactsToolbar";
import { ContactsTable } from "./ContactsTable";
import { BulkOutreachModal } from "./BulkOutreachModal";
import { AddContactModal } from "./AddContactModal";
import { TablePagination } from "../../../components/TablePagination";
import {
  contactsFilterKey,
  contactsScrollKey,
  loadCrmViewState,
  saveCrmViewState,
} from "../utils/crmViewState";
import { useCrmScrollRestoration } from "../hooks/useCrmScrollRestoration";
import "./ContactListPage.css";

type ContactType = "Coach" | "ProspectProfile" | "Parent";

const TYPE_HEADINGS: Record<ContactType, string> = {
  Coach: "Coaches",
  ProspectProfile: "Players",
  Parent: "Parents",
};

const FILTER_PARAM_KEYS = [
  "search",
  "divisions",
  "states",
  "projectedDivisions",
  "pipelineStages",
  "page",
];

const parseList = (val: string | null): string[] =>
  val ? val.split(",").filter(Boolean) : [];

const readFilterState = (params: URLSearchParams) => ({
  search: params.get("search") || "",
  divisions: parseList(params.get("divisions")),
  states: parseList(params.get("states")),
  projectedDivisions: parseList(params.get("projectedDivisions")),
  pipelineStages: parseList(params.get("pipelineStages")),
  currentPage: parseInt(params.get("page") || "1", 10),
});

const hasAnyFilterParam = (params: URLSearchParams): boolean =>
  FILTER_PARAM_KEYS.some((key) => params.has(key));

const getInitialFilterState = (
  searchParams: URLSearchParams,
  contactType: ContactType
) => {
  if (hasAnyFilterParam(searchParams)) {
    return readFilterState(searchParams);
  }
  const stored = loadCrmViewState(contactsFilterKey(contactType));
  return stored ? readFilterState(stored) : readFilterState(new URLSearchParams());
};

export const ContactListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const contactType = (searchParams.get("type") as ContactType) ?? "Coach";
  const initialState = getInitialFilterState(searchParams, contactType);

  const [contacts, setContacts] = useState<UnifiedContactResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);

  const [search, setSearch] = useState(initialState.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialState.search);
  const [divisions, setDivisions] = useState<string[]>(initialState.divisions);
  const [states, setStates] = useState<string[]>(initialState.states);
  const [projectedDivisions, setProjectedDivisions] = useState<string[]>(initialState.projectedDivisions);
  const [pipelineStages, setPipelineStages] = useState<string[]>(initialState.pipelineStages);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageCache = useRef<Map<string, ContactListPageResponse>>(new Map());
  const clearCache = () => { pageCache.current.clear(); };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== debouncedSearch) {
        setDebouncedSearch(search);
        setCurrentPage(1);
        setSelectedIds(new Set());
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Sync state to URL params (and mirror filter-only params into sessionStorage).
  // Runs on first mount too so that filters hydrated from sessionStorage are reflected in the URL.
  useEffect(() => {
    const filterParams = new URLSearchParams();
    if (debouncedSearch) filterParams.set("search", debouncedSearch);
    if (divisions.length > 0) filterParams.set("divisions", divisions.join(","));
    if (states.length > 0) filterParams.set("states", states.join(","));
    if (projectedDivisions.length > 0) filterParams.set("projectedDivisions", projectedDivisions.join(","));
    if (pipelineStages.length > 0) filterParams.set("pipelineStages", pipelineStages.join(","));
    if (currentPage > 1) filterParams.set("page", currentPage.toString());

    const urlParams = new URLSearchParams(filterParams);
    urlParams.set("type", contactType);

    setSearchParams(urlParams, { replace: true });
    saveCrmViewState(contactsFilterKey(contactType), filterParams);
  }, [debouncedSearch, divisions, states, projectedDivisions, pipelineStages, currentPage, contactType, setSearchParams]);

  // When contactType changes (e.g. sidebar switches Coaches → Players), rehydrate filters
  // from the new type's storage *during render* — not in an effect — so the URL-sync effect
  // above never observes stale state from the previous type and never leaks its filters into
  // the new type's URL or storage. (React's recommended "adjusting state during render" pattern.)
  const [prevContactType, setPrevContactType] = useState(contactType);
  if (prevContactType !== contactType) {
    setPrevContactType(contactType);
    const stored = loadCrmViewState(contactsFilterKey(contactType));
    const next = readFilterState(stored ?? new URLSearchParams());
    setDivisions(next.divisions);
    setStates(next.states);
    setProjectedDivisions(next.projectedDivisions);
    setPipelineStages(next.pipelineStages);
    setSearch(next.search);
    setDebouncedSearch(next.search);
    setCurrentPage(next.currentPage);
    setSelectedIds(new Set());
    setError(null);
    pageCache.current.clear();
  }

  const handleDivisionsChange = (val: string[]) => {
    setDivisions(val);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleStatesChange = (val: string[]) => {
    setStates(val);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleProjectedDivisionsChange = (val: string[]) => {
    setProjectedDivisions(val);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handlePipelineStagesChange = (val: string[]) => {
    setPipelineStages(val);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  // The filters the export must reuse verbatim, so the CSV covers exactly the
  // set the user is looking at rather than the current page or an unfiltered pull.
  const filterParams = {
    search: debouncedSearch || undefined,
    divisions: divisions.length > 0 ? divisions : undefined,
    states: states.length > 0 ? states : undefined,
    projectedDivisions: projectedDivisions.length > 0 ? projectedDivisions : undefined,
    pipelineStages: pipelineStages.length > 0 ? pipelineStages : undefined,
    contactType: contactType || undefined,
  };

  const handleExportEmails = async () => {
    setError(null);
    setExporting(true);
    try {
      const blob = await exportContactEmailList(filterParams);
      const date = new Date().toISOString().split("T")[0];
      downloadBlobFile(blob, `crm_email_list_${date}.csv`);
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to export email list"));
      console.error("Failed to export contact email list:", err);
    } finally {
      setExporting(false);
    }
  };

  const fetchContacts = useCallback(async (skipCache = false) => {
    const params = {
      search: debouncedSearch || undefined,
      divisions: divisions.length > 0 ? divisions : undefined,
      states: states.length > 0 ? states : undefined,
      projectedDivisions: projectedDivisions.length > 0 ? projectedDivisions : undefined,
      pipelineStages: pipelineStages.length > 0 ? pipelineStages : undefined,
      contactType: contactType || undefined,
      pageNumber: currentPage,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    const cacheKey = JSON.stringify(params);

    if (!skipCache) {
      const cached = pageCache.current.get(cacheKey);
      if (cached) {
        setContacts(cached.items);
        setTotalCount(cached.totalCount);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      const data = await getContacts(params);
      pageCache.current.set(cacheKey, data);
      setContacts(data.items);
      setTotalCount(data.totalCount);
    } catch (err) {
      console.error("Failed to load contacts:", err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, divisions, states, projectedDivisions, pipelineStages, contactType, currentPage]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useCrmScrollRestoration(
    contactsScrollKey(contactType),
    !loading && contacts.length > 0
  );

  const handleSelectionChange = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleRowClick = (contact: UnifiedContactResponse) => {
    const from = `${location.pathname}${location.search}`;
    navigate(`/crm/contacts/${contact.id}`, { state: { contactType, from } });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedIds(new Set());
  };

  const selectedContacts = contacts.filter((c) => selectedIds.has(c.id));

  return (
    <div className="contact-list-page">
      <div className="contact-list-page__header">
        <h1 className="contact-list-page__title">{TYPE_HEADINGS[contactType]}</h1>
        {totalCount > 0 && (
          <span className="contact-list-page__count">{totalCount.toLocaleString()}</span>
        )}
      </div>

      <ContactsToolbar
        search={search}
        onSearchChange={setSearch}
        divisions={divisions}
        onDivisionsChange={handleDivisionsChange}
        states={states}
        onStatesChange={handleStatesChange}
        projectedDivisions={projectedDivisions}
        onProjectedDivisionsChange={handleProjectedDivisionsChange}
        pipelineStages={pipelineStages}
        onPipelineStagesChange={handlePipelineStagesChange}
        contactType={contactType}
        selectedContacts={selectedContacts}
        onBulkSend={() => setIsBulkModalOpen(true)}
        onAddContact={() => setIsAddModalOpen(true)}
        onExportEmails={handleExportEmails}
        exporting={exporting}
      />

      {error && (
        <div className="contact-list-page__error" role="alert">
          {error}
        </div>
      )}

      <div className="contact-list-page__table-wrapper">
        <ContactsTable
          contacts={contacts}
          loading={loading}
          selectedIds={selectedIds}
          contactType={contactType}
          onSelectionChange={handleSelectionChange}
          onSelectAll={handleSelectAll}
          onRowClick={handleRowClick}
          openContactId={null}
        />
      </div>

      <div className="contact-list-page__pagination">
        <TablePagination
          currentPage={currentPage}
          pageSize={DEFAULT_PAGE_SIZE}
          totalCount={totalCount}
          onPageChange={handlePageChange}
        />
      </div>

      <BulkOutreachModal
        isOpen={isBulkModalOpen}
        contacts={selectedContacts}
        onClose={() => setIsBulkModalOpen(false)}
      />

      <AddContactModal
        isOpen={isAddModalOpen}
        contactType={contactType}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setIsAddModalOpen(false);
          clearCache();
          fetchContacts(true);
        }}
      />
    </div>
  );
};
